import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar as CalendarComponent } from './ui/calendar';
import { mockServices, mockBookingSlots, mockFormSubmission } from './mock';

const BookingSection = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  // Get available time slots for selected date
  const getAvailableSlots = (date) => {
    if (!date) return [];
    const dateString = date.toISOString().split('T')[0];
    const slot = mockBookingSlots.find(slot => slot.date === dateString);
    return slot ? slot.slots : ['10:00 AM', '2:00 PM', '4:00 PM']; // Default slots
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const submissionData = {
      ...formData,
      service: selectedService,
      date: selectedDate?.toDateString(),
      time: selectedTime
    };

    try {
      const result = await mockFormSubmission(submissionData);
      setConfirmationMessage(result.message);
      setIsSubmitted(true);
      
      // Reset form after successful submission
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', message: '' });
        setSelectedDate(null);
        setSelectedTime('');
        setSelectedService('');
      }, 5000);
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return formData.name && formData.email && selectedService && selectedDate && selectedTime;
  };

  if (isSubmitted) {
    return (
      <section id="booking-form" className="py-20 bg-gradient-to-br from-[#5f9dff]/5 to-[#61525a]/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h3 className="text-2xl font-bold text-[#1e1919] mb-4 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                Booking Confirmed!
              </h3>
              <p className="text-lg text-[#736c64] mb-6">
                {confirmationMessage}
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="bg-[#61525a] hover:bg-[#4a3f47] text-white px-8 py-3 rounded-xl font-semibold"
              >
                Book Another Session
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="booking-form" className="py-20 bg-gradient-to-br from-[#5f9dff]/5 to-[#61525a]/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e1919] mb-6 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            Book a 
            <span className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] bg-clip-text text-transparent">Service Inquiry</span>
          </h2>
          <p className="text-lg text-[#736c64] max-w-3xl mx-auto leading-relaxed">
            Ready to transform your business or academic journey? Schedule a consultation with our expert team.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Booking Form */}
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                Schedule Your Consultation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Service Selection */}
                <div>
                  <Label htmlFor="service" className="text-[#61525a] font-semibold mb-2 block">
                    Select Service *
                  </Label>
                  <Select value={selectedService} onValueChange={setSelectedService}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Choose your area of interest" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockServices.map((service) => (
                        <SelectItem key={service.id} value={service.title}>
                          <div className="py-2">
                            <div className="font-medium">{service.title}</div>
                            <div className="text-sm text-gray-500">{service.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Personal Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-[#61525a] font-semibold mb-2 block">
                      Full Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="pl-10 h-12"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-[#61525a] font-semibold mb-2 block">
                      Email Address *
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="pl-10 h-12"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#61525a] font-semibold mb-2 block">
                    Phone Number
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label htmlFor="message" className="text-[#61525a] font-semibold mb-2 block">
                    Project Details
                  </Label>
                  <Textarea
                    id="message"
                    placeholder="Tell us about your project, goals, and specific requirements..."
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className="min-h-[120px] resize-none"
                  />
                </div>

                {/* Time Selection */}
                {selectedDate && (
                  <div>
                    <Label className="text-[#61525a] font-semibold mb-2 block">
                      Select Time *
                    </Label>
                    <div className="grid grid-cols-3 gap-3">
                      {getAvailableSlots(selectedDate).map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setSelectedTime(slot)}
                          className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                            selectedTime === slot
                              ? 'border-[#5f9dff] bg-[#5f9dff] text-white'
                              : 'border-gray-200 hover:border-[#5f9dff]/50 hover:bg-[#5f9dff]/10'
                          }`}
                        >
                          <Clock className="w-4 h-4 mx-auto mb-1" />
                          <span className="text-sm font-medium">{slot}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={!isFormValid() || isSubmitting}
                  className="w-full bg-[#61525a] hover:bg-[#4a3f47] text-white h-12 text-lg font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Send className="w-5 h-5 mr-2" />
                      Book Consultation
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Calendar */}
          <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-[#1e1919] font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif] flex items-center">
                <Calendar className="w-6 h-6 mr-2 text-[#5f9dff]" />
                Select Date *
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || date < new Date(new Date().setDate(new Date().getDate() - 1))}
                className="rounded-md border-0 w-full"
              />
              
              {selectedDate && (
                <div className="mt-6 p-4 bg-[#5f9dff]/10 rounded-lg">
                  <h4 className="font-semibold text-[#61525a] mb-2">Selected Date:</h4>
                  <p className="text-[#1e1919]">{selectedDate.toDateString()}</p>
                  {selectedTime && (
                    <>
                      <h4 className="font-semibold text-[#61525a] mt-3 mb-2">Selected Time:</h4>
                      <p className="text-[#1e1919]">{selectedTime}</p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export { BookingSection };